import React, { useEffect, useState, useRef } from 'react';
import { initializeModal } from '../../utils/ModalUtils';
import { generateBlueprint, saveGeneratedTemplate } from '../../services/StrategyTemplatesService';
import { processError } from '../../services/ServiceUtils';
import BlueprintGateResults from './BlueprintGateResults';

// Fase 2d.2 gate e — UI admin de GERAÇÃO de estratégia por IA. Fluxo num só modal: (1) descreve a intenção
// → a IA propõe um BLUEPRINT (classe segura: spot/indicador, ação ORDER); (2) o backend já roda os gates
// schema+sim+advisory e devolve o veredito; (3) o admin revisa; (4) salva como template (só se passou nos
// gates determinísticos). Human-in-the-loop. Ver project_fase2d_blueprint_design (gate e).

const DIFFICULTIES = ['Fácil', 'Média', 'Difícil'];

const EXAMPLES = [
    { label: 'Reversão (RSI)', text: 'Compra quando o RSI de 14 fica abaixo de 30 (sobrevendido) e vende quando passa de 65. Não recomprar posição aberta.' },
    { label: 'Tendência (EMA)', text: 'Cruzamento de médias: compra quando a EMA de 9 cruza acima da EMA de 21 e vende no cruzamento inverso.' },
];

function GenerateStrategyModal(props) {
    const btnClose = useRef('');
    const [intent, setIntent] = useState('');
    const [interval, setIntervalCfg] = useState('1h');
    const [walletPercent, setWalletPercent] = useState(20);
    const [generating, setGenerating] = useState(false);
    const [cand, setCand] = useState(null);         // { blueprint, ok, gates:{schema,sim,advisory}, _servedBy }
    const [error, setError] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [difficulty, setDifficulty] = useState('Média');
    const [saving, setSaving] = useState(false);

    function reset() {
        setIntent(''); setIntervalCfg('1h'); setWalletPercent(20);
        setGenerating(false); setCand(null); setError(''); setName(''); setDescription(''); setDifficulty('Média'); setSaving(false);
    }

    useEffect(() => { initializeModal('modalGenerateStrategy', reset); }, []);

    function onGenerate() {
        if (!intent.trim()) { setError('Descreva o que a estratégia deve fazer.'); return; }
        setGenerating(true); setError(''); setCand(null);
        generateBlueprint({ intent: intent.trim(), config: { interval, walletPercent: Number(walletPercent) || 20 } })
            .then(res => {
                setGenerating(false);
                setCand(res);
                // Prefill do nome a partir do padrão do blueprint (troca placeholders por um exemplo legível).
                const pat = res && res.blueprint && res.blueprint.strategyNamePattern;
                if (pat) setName(String(pat).replace(/\$\{symbol\}/g, 'BTCUSDT').replace(/\$\{interval\}/g, interval));
            })
            .catch(err => { setGenerating(false); setError(processError(err)); });
    }

    function onSave() {
        if (!cand || !cand.ok) return;
        setSaving(true); setError('');
        saveGeneratedTemplate({ blueprint: cand.blueprint, name: name.trim(), description, difficulty })
            .then(() => { setSaving(false); if (btnClose.current) btnClose.current.click(); if (props.onSaved) props.onSaved(); })
            .catch(err => {
                setSaving(false);
                const data = err && err.response && err.response.data;
                setError(data && data.error ? data.error : processError(err));
            });
    }

    const gates = cand && cand.gates;

    return (
        <div className="modal fade" id="modalGenerateStrategy" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title">✨ Gerar estratégia por IA</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">

                        <div className="alert alert-info py-2 small mb-3">
                            Descreva em português o que a estratégia deve fazer. A IA cria uma estratégia <strong>spot baseada em indicadores</strong> (só compra/venda a mercado — sem futuros, grid ou saque). Antes de salvar, ela passa por <strong>validação de segurança + simulação</strong>. Você revisa e decide.
                        </div>

                        <div className="mb-2">
                            <label htmlFor="genIntent">O que a estratégia deve fazer?</label>
                            <textarea className="form-control" id="genIntent" rows="3" value={intent}
                                placeholder={EXAMPLES[0].text} onChange={e => setIntent(e.target.value)} />
                            <div className="mt-2 d-flex align-items-center flex-wrap gap-2">
                                <span className="text-muted small">Começar de um exemplo:</span>
                                {EXAMPLES.map((ex, i) => (
                                    <button key={i} type="button" className="btn btn-sm btn-outline-primary"
                                        onClick={() => setIntent(ex.text)}>{ex.label}</button>
                                ))}
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-6 col-md-3">
                                <label htmlFor="genInterval">Tempo gráfico</label>
                                <input className="form-control form-control-sm" id="genInterval" value={interval}
                                    onChange={e => setIntervalCfg(e.target.value)} placeholder="1h" />
                            </div>
                            <div className="col-6 col-md-3">
                                <label htmlFor="genWallet">% da carteira</label>
                                <input className="form-control form-control-sm" id="genWallet" type="number" value={walletPercent}
                                    onChange={e => setWalletPercent(e.target.value)} />
                            </div>
                            <div className="col-12 col-md-6 d-flex align-items-end">
                                <button type="button" className="btn btn-primary w-100" onClick={onGenerate} disabled={generating}>
                                    {generating ? 'Gerando e validando…' : (cand ? 'Gerar de novo' : 'Gerar estratégia')}
                                </button>
                            </div>
                        </div>

                        {error && <div className="alert alert-danger py-2">{error}</div>}

                        {cand && gates && (
                            <React.Fragment>
                                <hr />
                                <BlueprintGateResults cand={cand} />

                                {/* Salvar (só se aprovada) */}
                                {cand.ok && (
                                    <React.Fragment>
                                        <hr />
                                        <h6>Salvar como template</h6>
                                        <div className="row">
                                            <div className="col-md-8 mb-2">
                                                <label htmlFor="genName">Nome</label>
                                                <input className="form-control form-control-sm" id="genName" value={name}
                                                    onChange={e => setName(e.target.value)} />
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <label htmlFor="genDiff">Dificuldade</label>
                                                <select className="form-select form-select-sm" id="genDiff" value={difficulty}
                                                    onChange={e => setDifficulty(e.target.value)}>
                                                    {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                                <small className="text-muted d-block mt-1">Só um rótulo de complexidade que o trader vê na galeria — <strong>não muda nada na execução</strong>.</small>
                                            </div>
                                            <div className="col-12 mb-2">
                                                <label htmlFor="genDesc">Descrição (opcional)</label>
                                                <textarea className="form-control form-control-sm" id="genDesc" rows="2" value={description}
                                                    onChange={e => setDescription(e.target.value)} />
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )}
                            </React.Fragment>
                        )}

                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Fechar</button>
                        <button type="button" className="btn btn-success" onClick={onSave}
                            disabled={saving || !cand || !cand.ok || !name.trim()}>
                            {saving ? 'Salvando…' : 'Salvar template'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GenerateStrategyModal;
